import { useState, useEffect, useMemo, useRef } from "react";
import { usePost } from "./usePost";
import {
  CreateTaskConfig,
  CreateTaskParams,
  Note,
  Task,
  TaskColumnType,
  TaskStatus,
  UpdateTaskConfig,
  WsQParamType,
} from "../models/workflowModels";
import { ColumnType, TaskDataSource } from "../models/tableModels";
import {
  buildTaskDataSource,
  convertConfigToParams,
  convertConfigToUpdateParams,
  generateMockTaskData,
} from "../pages/Workspace/utils";
import {
  TASK_COLUMNS,
  TASK_COLUMN_TYPES,
  DELETE_COLUMN,
} from "../pages/Workspace/constants";
import { useToast } from "../store/ToastProvider";
import { useNavigate, useParams } from "react-router-dom";
import { ROUTES } from "../routes/routeNames";
import { AvatarProps } from "../components/visuals/Avatar";

export const useWorkspace = (): UseWorkspace => {
  const { addToast } = useToast();
  const { paramType } = useParams();
  const navigate = useNavigate();
  const [tasks, setTasks] = useState<TaskDataSource[]>([]);
  const [selectedColumns, setSelectedColumns] =
    useState<TaskColumnType[]>(TASK_COLUMNS);

  const [displayNoteModal, setDisplayNoteModal] = useState<boolean>(false);
  const [displayTaskModal, setDisplayTaskModal] = useState<boolean>(false);
  const paramTypeRef = useRef(paramType);

  // Keep ref in sync so callbacks always see the latest value
  useEffect(() => {
    paramTypeRef.current = paramType;
  }, [paramType]);

  const mockData: TaskDataSource[] = useMemo(() => generateMockTaskData(), []);

  const {
    sendData: getTasks,
    data: rawTasks,
    isLoading: isLoadingTasks,
    error: getTasksError,
  } = usePost<Task[], null>();

  const {
    sendData: deleteTask,
    data: deleteTaskData,
    isLoading: isDeleting,
    error: deleteTaskError,
  } = usePost<boolean, { task_id: string }>();

  const {
    sendData: addNote,
    data: addNoteResult,
    error: addNoteError,
  } = usePost<{ success: boolean }, Note>();

  const { sendData: createTask, data: addTaskResult } = usePost<
    { success: boolean },
    CreateTaskParams
  >();

  const { data: updatedTaskResult, sendData: updateTask } = usePost<
    { success: boolean },
    UpdateTaskConfig
  >();

  useEffect(() => {
    setTasks(mockData);
  }, []);

  useEffect(() => {
    const shouldDisplayTaskModal: boolean =
      paramType === WsQParamType.VIEW_TASK ||
      paramType === WsQParamType.ADD_TASK;
    const shouldDisplayNoteModal: boolean =
      paramType === WsQParamType.ADD_NOTE ||
      paramType === WsQParamType.VIEW_NOTE;

    setDisplayNoteModal(shouldDisplayNoteModal);
    setDisplayTaskModal(shouldDisplayTaskModal);
  }, [paramType]);

  useEffect(() => {
    getTasks("GET_TASKS", null);
    deleteTaskError && addToast("Error Deleting task", "error");
    addNoteResult && addToast("Note added successfully", "success");
    addTaskResult && addToast("Task added successfully", "success");
    updatedTaskResult && addToast("Task updated successfully", "success");
  }, [deleteTaskError, addNoteResult, addTaskResult, updatedTaskResult]);

  useEffect(() => {
    getTasksError && addToast("Error Fetching tasks", "error");
    addNoteError && addToast("Error Adding Note", "error");
  }, [getTasksError, addNoteError]);

  useEffect(() => {
    if (deleteTaskData) {
      addToast("Task deleted successfully", "success");
    }
  }, [deleteTaskData]);

  const handleDeleteTask = (id: string | number): void => {
    const task_id = id as string;
    deleteTask("DELETE_TASK", { task_id });
    setTasks((currentTasks) =>
      currentTasks.filter((task) => task.key !== task_id),
    );
  };

  // Dynamically inject assignee filter options into the assigned_to column
  const columnsWithDynamicFilters: TaskColumnType[] = useMemo(() => {
    const seen = new Set<string>();
    const assigneeFilters = tasks
      .map((t) => String((t.assigned_to as AvatarProps)?.username || ""))
      .filter((u) => {
        if (!u || seen.has(u)) return false;
        seen.add(u);
        return true;
      })
      .map((u) => ({ text: u, value: u }));

    return selectedColumns.map((col) =>
      col.key === "assigned_to" && assigneeFilters.length > 0
        ? { ...col, filters: assigneeFilters }
        : col,
    );
  }, [selectedColumns, tasks]);

  const onHideModal = (): void => {
    navigate(ROUTES.WORKSPACE);
  };

  // Separate handler for the task modal — when the task modal closes because
  // we navigated to a note route, skip the navigate-back so the note modal
  // can open.  We use a ref to read the latest paramType, avoiding stale
  // closure issues when React-Bootstrap calls onHide.
  const onHideTaskModal = (): void => {
    const current = paramTypeRef.current;
    if (
      current === WsQParamType.VIEW_NOTE ||
      current === WsQParamType.ADD_NOTE
    ) {
      return;
    }
    navigate(ROUTES.WORKSPACE);
  };

  const handleDisplayTaskModal = (): void => {
    navigate(`${ROUTES.WORKSPACE}/${WsQParamType.ADD_TASK}`);
  };

  useEffect(() => {
    if (rawTasks) {
      setTasks(buildTaskDataSource(rawTasks, handleDeleteTask));
    }
  }, [rawTasks]);

  const handleEditClick = (): void => {
    const hasDeleteColumn = selectedColumns.some(
      (column) => column.key === DELETE_COLUMN.key,
    );
    setSelectedColumns(
      hasDeleteColumn
        ? selectedColumns.filter((column) => column.key !== DELETE_COLUMN.key)
        : [...selectedColumns, DELETE_COLUMN],
    );
  };

  const handleAddNote = (note: Note): void => {
    if (!note.title || !note.content) {
      addToast("Note title and content are required", "error");
      return;
    }
    addNote("ADD_TASK_NOTE", note);
    navigate(ROUTES.WORKSPACE);
  };

  const handleAddTask = (task: CreateTaskConfig): void => {
    const params = convertConfigToParams(task);
    if (!params) {
      addToast("Please fill in required parameters to create task", "error");
      return;
    }
    createTask("CREATE_TASK", params);
    navigate(ROUTES.WORKSPACE);
  };

  const handleUpdateTask = (
    task: CreateTaskConfig,
    taskId: string,
    status: TaskStatus,
  ): void => {
    const params = convertConfigToUpdateParams(task, taskId, status);
    if (!params) {
      addToast("Please fill in required parameters to update", "error");
      return;
    }
    updateTask("UPDATE_TASK", params);
    navigate(ROUTES.WORKSPACE);
  };

  return {
    dataSource: tasks,
    isLoading: isLoadingTasks || isDeleting,
    columns: columnsWithDynamicFilters,
    columnTypes: TASK_COLUMN_TYPES,
    handleEdit: handleEditClick,
    handleAddNote,
    handleAddTask,
    handleUpdateTask,
    displayNoteModal,
    onHideModal,
    onHideTaskModal,
    displayTaskModal,
    handleDisplayTaskModal,
  };
};

export type UseWorkspace = {
  dataSource: TaskDataSource[];
  isLoading: boolean;
  columns: TaskColumnType[];
  columnTypes: ColumnType[];
  handleEdit: () => void;
  handleAddNote: (note: Note) => void;
  handleAddTask: (task: CreateTaskConfig) => void;
  handleUpdateTask: (
    task: CreateTaskConfig,
    taskId: string,
    status: TaskStatus,
  ) => void;
  displayNoteModal: boolean;
  onHideModal: () => void;
  onHideTaskModal: () => void;
  displayTaskModal: boolean;
  handleDisplayTaskModal: () => void;
};
